import Refresh from "@mui/icons-material/Refresh";
import {
	Box,
	CircularProgress,
	FormControlLabel,
	FormLabel,
	ListSubheader,
	MenuItem,
	Radio,
	RadioGroup,
	Stack,
	Typography
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import type { RuleVariable } from "../hooks/useRuleVariables";
import type { TableOption, TableSource } from "../types/configs";
import { partitionScreenTablesForLoop } from "../utils/loop-resource-variables";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface TableGroup {
	label: string;
	icon?: React.ReactElement;
	countLabel?: string;
	options: TableOption[];
	source: TableSource;
}

type LoopDirection = "firstToLast" | "lastToFirst";

export type LoopFormIterateType = "variable" | "table";

export interface LoopFormState {
	loopName: string;
	/** Saved on the loop node as `data.loopType`. */
	loopType: LoopFormIterateType;
	databaseTableId?: string;
	screenTableId?: string;
	databaseTableVariableId?: string;
	/** Multi-row DB table variable display name; used as the server field `databaseTableVariableName`. */
	databaseTableVariableName?: string;
	/** Set when `loopType === "table"` and a table row is selected. */
	tableName?: string;
	/** Only meaningful when `loopType === "table"`. */
	direction?: LoopDirection;
	collectionId?: string;
	collectionName?: string;
}

export interface LoopFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: LoopFormState) => void;
	initialState?: Partial<LoopFormState>;
	isSaving?: boolean;
	platformId: string | number;
	getDuplicateNameError?: (name: string) => string | undefined;
	/**
	 * Set of loop names already in use on the same canvas level. Used to auto-increment
	 * the default loop name and block saves when a collision is detected. When
	 * `getDuplicateNameError` is also provided, both validators are applied and the first
	 * non-undefined message wins.
	 */
	takenLoopNames?: Set<string>;
	/** Multi-row database table variables from rule state (not loaded from the table-options API). */
	ruleDatabaseMultiRowVariables?: RuleVariable[];
	/** Rule screen location from Create Rule modal — drives Screen Tables group. */
	ruleScreenLocationId?: string | number;
	collectionOptions?: { label: string; value: string }[];
}

type LoopFormInternalState = {
	loopName: string;
	loopType: LoopFormIterateType;
	table: string; // "<source>-<tableId>" composite for the select
	tableName: string;
	direction: LoopDirection;
};

/** Returns the lowest available name of the form `base`, `base 1`, `base 2`, … */
const getUniqueLoopName = (base: string, taken: Set<string>): string => {
	if (!taken.has(base.toLowerCase())) return base;
	let n = 1;
	while (taken.has(`${base} ${n}`.toLowerCase())) n += 1;
	return `${base} ${n}`;
};

const directionFromInitial = (initial?: Partial<LoopFormState>): LoopDirection =>
	initial?.direction === "lastToFirst" ? "lastToFirst" : "firstToLast";

/** Table select value + label from saved node / API initial state only (not user edits). */
const tableFieldsFromInitialState = (
	initial: Partial<LoopFormState> | undefined,
	multiRowVars: RuleVariable[],
): Pick<LoopFormInternalState, "table" | "tableName"> => {
	if (!initial) return { table: "", tableName: "" };
	let table = "";
	if (initial.screenTableId) {
		table = `screen-${initial.screenTableId}`;
	} else if (initial.databaseTableVariableName) {
		const match = multiRowVars.find((v) => v.name === initial.databaseTableVariableName);
		if (match) table = `databaseVariable-${match.id}`;
	} else if (initial.databaseTableVariableId) {
		table = `databaseVariable-${initial.databaseTableVariableId}`;
	} else if (initial.databaseTableId) {
		table = `database-${initial.databaseTableId}`;
	} else if (initial.collectionId) {
		table = `collection-${initial.collectionId}`;
	}
	return {
		table,
		tableName: initial.collectionName ?? initial.tableName ?? "",
	};
};

const LoopForm = (props: LoopFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		platformId,
		getDuplicateNameError,
		takenLoopNames,
		ruleDatabaseMultiRowVariables = [],
		ruleScreenLocationId,
		collectionOptions = [],
	} = props;

	const [fetchedScreenTableGroups, setFetchedScreenTableGroups] = useState<TableGroup[]>([]);
	const [isLoadingTables, setIsLoadingTables] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);

	const authHeader = useAuthHeader();
	const [formState, setFormState] = useState<LoopFormInternalState>({
		loopName: initialState?.loopName ?? "",
		loopType: initialState?.loopType ?? "variable",
		table: "",
		tableName: "",
		direction: "firstToLast"
	});

	const multiRowTableGroup = useMemo((): TableGroup | null => {
		if (!ruleDatabaseMultiRowVariables.length) return null;
		return {
			label: "Database Table Variable - Multi Row",
			source: "databaseVariable",
			options: ruleDatabaseMultiRowVariables.map((v) => ({
				label: v.name,
				value: v.id,
				source: "databaseVariable" as const,
			})),
		};
	}, [ruleDatabaseMultiRowVariables]);

	const collectionTableGroup = useMemo((): TableGroup | null => {
		if (!collectionOptions.length) return null;
		return {
			label: "Collections",
			source: "collection",
			options: collectionOptions.map((c) => ({
				label: c.label,
				value: c.value,
				source: "collection" as const,
			})),
		};
	}, [collectionOptions]);

	const tableGroups = useMemo(() => {
		const merged: TableGroup[] = [...fetchedScreenTableGroups];
		if (collectionTableGroup) merged.push(collectionTableGroup);
		if (multiRowTableGroup) merged.push(multiRowTableGroup);
		return merged;
	}, [fetchedScreenTableGroups, collectionTableGroup, multiRowTableGroup]);

	const handleTableChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const value = event.target.value as string;
		const selectedOption = tableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === value);

		setFormState((previous) => ({
			...previous,
			table: value,
			tableName: selectedOption?.label ?? ""
		}));
	};

	// The name the form should start with: the existing node name (for edits) or the
	// next available incremented name (for new nodes), mirroring how FilterForm uses
	// initialState.filterName to avoid default-name collisions.
	const defaultLoopName = useMemo((): string => {
		if (initialState?.loopName) return initialState.loopName;
		if (takenLoopNames) return getUniqueLoopName("Loop", takenLoopNames);
		return "";
	}, [initialState?.loopName, takenLoopNames]);

	const initialLoopType = (initialState?.loopType ?? "variable") as LoopFormIterateType;

	useEffect(() => {
		if (!open || !platformId) return;

		const tableFields = tableFieldsFromInitialState(initialState, ruleDatabaseMultiRowVariables);
		// Full sync from API / parent when this run starts — only `loopType` / `loopName`
		// / table binding from initialState; never overwritten again when the table fetch finishes.
		setFormState({
			loopName: defaultLoopName,
			loopType: initialLoopType,
			...tableFields,
			direction: directionFromInitial(initialState),
		});

		const fetchTables = async () => {
			setIsLoadingTables(true);
			setLoadError(null);

			try {
				const response = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
				
				if (response.status !== 200) {
					throw new Error(`Failed to fetch tables: ${response.statusText}`);
				}

				const data = response.data;
				const rawGroups = data.tableGroups || [];
				const { currentScreenTables, otherScreenTables } = partitionScreenTablesForLoop(
					rawGroups,
					ruleScreenLocationId,
				);

				setFetchedScreenTableGroups([
					{ label: "Screen Tables", source: "screen", options: currentScreenTables },
					{ label: "Other Screens with Tables", source: "screen", options: otherScreenTables },
				]);

			} catch (error) {
				console.error("Error fetching tables:", error);
				setLoadError(error instanceof Error ? error.message : "Failed to load tables");
				
				setFetchedScreenTableGroups([]);
				setFormState((prev) => ({
					...prev,
					table: "",
					tableName: "",
				}));
			} finally {
				setIsLoadingTables(false);
			}
		};

		fetchTables();
	}, [open, platformId, defaultLoopName, initialLoopType, initialState, ruleDatabaseMultiRowVariables, ruleScreenLocationId, authHeader]);

	const duplicateNameError = useMemo((): string | undefined => {
		const trimmed = formState.loopName.trim();
		if (!trimmed) return undefined;
		const fromProp = getDuplicateNameError?.(trimmed);
		if (fromProp) return fromProp;
		if (takenLoopNames && initialState?.loopName?.trim() !== trimmed && takenLoopNames.has(trimmed)) {
			return "This name is already used by another loop node.";
		}
		return undefined;
	}, [getDuplicateNameError, takenLoopNames, formState.loopName, initialState?.loopName]);

	const isSaveDisabled = (() => {
		if (!formState.loopName.trim() || Boolean(duplicateNameError)) return true;
		if (formState.loopType === "variable") return false;
		return !formState.table || isLoadingTables;
	})();

	const handleSave = () => {
		if (formState.loopType === "variable") {
			onSave({
				loopName: formState.loopName.trim(),
				loopType: "variable",
			});
			return;
		}

		const [source, ...rest] = formState.table.split("-");
		const tableId = rest.join("-");
		const selectedOption = tableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === formState.table);

		if (source === "databaseVariable") {
			onSave({
				loopName: formState.loopName.trim(),
				loopType: "table",
				tableName: "",
				databaseTableVariableName: selectedOption?.label ?? formState.tableName,
				direction: formState.direction,
			});
			return;
		}

		if (source === "collection") {
			onSave({
				loopName: formState.loopName.trim(),
				loopType: "table",
				collectionId: tableId,
				collectionName: selectedOption?.label ?? formState.tableName,
				direction: formState.direction,
			});
			return;
		}

		onSave({
			loopName: formState.loopName.trim(),
			loopType: "table",
			tableName: selectedOption?.label ?? formState.tableName,
			databaseTableId: source === "database" ? tableId : undefined,
			screenTableId: source === "screen" ? tableId : undefined,
			direction: formState.direction,
		});
	};

	const renderTableOptions = () => {
		if (isLoadingTables) {
			return (
				<MenuItem disabled>
					<CircularProgress size={16} sx={{ mr: 1 }} />
					Loading...
				</MenuItem>
			);
		}

		if (loadError) {
			return <MenuItem disabled sx={{ color: "error.main" }}>Error loading tables</MenuItem>;
		}

		// MUI Select requires MenuItem/ListSubheader as direct children (no Fragment wrapper).
		return tableGroups.flatMap((group, groupIndex) => {
			const options = group.options ?? [];
			return [
				<ListSubheader key={`header-${groupIndex}`} sx={{ fontWeight: "bold", color: "text.primary" }}>
					{group.label}
				</ListSubheader>,
				...(options.length > 0
					? options.map((option) => (
						<MenuItem key={`${group.source}-${option.value}`} value={`${group.source}-${option.value}`}>
							{option.label}
						</MenuItem>
					))
					: [
						<MenuItem
							key={`empty-${groupIndex}`}
							disabled
							sx={{ fontStyle: "italic" }}
						>
							No Tables
						</MenuItem>,
					]),
			];
		});
	};

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Loop"
			icon={<Refresh />}
			iconColor="#A434D9"
			gradient="linear-gradient(135deg, #D63AD9 0%, #8C30E0 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Loop"
			width={520}
		>
			{loadError && (
				<Typography color="error" sx={{ mb: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
					{loadError}
				</Typography>
			)}
			{/* No extra padding here — BaseForm's body already provides the
			    panel's horizontal padding. The previous `p: 2` double-padded the
			    content, pushing the fields/rows in past the full-width divider
			    lines and leaving white space on their left. */}
			<Stack spacing={3}>
				<CanvasFormTextField
					label="Loop Name"
					value={formState.loopName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							loopName: event.target.value
						}))
					}
					placeholder="e.g., Appointment Loop"
					required
					width="100%"
				/>
				<Box>
					<FormLabel component="legend" sx={{ mb: 1, color: "text.secondary", fontSize: "0.75rem" }}>
						Type
					</FormLabel>
					<RadioGroup
						row
						value={formState.loopType}
						onChange={(event) =>
							setFormState((previous) => ({
								...previous,
								loopType: event.target.value as LoopFormIterateType
							}))
						}
					>
						<FormControlLabel value="variable" control={<Radio />} label="Variable" />
						<FormControlLabel value="table" control={<Radio />} label="Table" />
					</RadioGroup>
				</Box>
				{formState.loopType === "table" && (
					<>
						<Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
							<CanvasFormTextField
								select
								label="Table"
								value={formState.table}
								onChange={handleTableChange}
								disabled={isLoadingTables}
								width="100%"
							>
								{renderTableOptions()}
							</CanvasFormTextField>
						</Stack>
						<Box>
							<Typography variant="subtitle2" color="text.secondary" gutterBottom>
								Direction
							</Typography>
							<RadioGroup
								row
								value={formState.direction}
								onChange={(event) =>
									setFormState((previous) => ({
										...previous,
										direction: event.target.value as LoopDirection
									}))
								}
							>
								<FormControlLabel
									value="firstToLast"
									control={<Radio />}
									label="First to Last"
									disabled={isLoadingTables}
								/>
								<FormControlLabel
									value="lastToFirst"
									control={<Radio />}
									label="Last to First"
									disabled={isLoadingTables}
								/>
							</RadioGroup>
						</Box>
					</>
				)}
			</Stack>
		</BaseForm>
	);
};

export default LoopForm;